import InputAdornment from '@mui/material/InputAdornment';
import { forwardRef } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { NumericFormat } from 'react-number-format';

import { useFieldContext } from '~/common/form/formContexts';

import Input from './InputBase/Input';

type NumericFormatProps = React.ComponentProps<typeof NumericFormat>;
type MuiInputType = Omit<
  React.ComponentProps<typeof Input>,
  'helperText' | 'defaultValue' | 'onChange'
>;

type CurrencyInputProps = Partial<MuiInputType> &
  Omit<Partial<NumericFormatProps>, 'onChange'> & {
    onChange?: NumericFormatProps['onValueChange'];
    min?: number;
    max?: number;
    errorMessage?: string;
    defaultValue?: string | number;
    useReactHooksForm?: boolean;
    useTanstackForm?: boolean;
    rules?: Record<string, any>;
  };

const CurrencyInput = forwardRef<
  React.ElementRef<typeof Input>,
  Readonly<CurrencyInputProps>
>(({ useReactHooksForm = false, useTanstackForm = false, ...props }, ref) => {
  if (useReactHooksForm) {
    return <HookFormCurrency {...props} />;
  }
  if (useTanstackForm) {
    return <TanstackFormCurrency {...props} ref={ref} />;
  }
  return <CurrencyBase {...props} ref={ref} />;
});

const HookFormCurrency = forwardRef<
  React.ElementRef<typeof Input>,
  Readonly<CurrencyInputProps>
>(({ name, rules, ...props }, ref) => {
  const {
    control,
    formState: { errors },
  } = useFormContext();

  return (
    <Controller
      name={name}
      control={control}
      rules={rules}
      render={({ onChange, ...hooksProps }, { invalid }) => (
        <CurrencyBase
          error={invalid}
          errorMessage={errors[name]?.message}
          {...props}
          {...hooksProps}
          ref={ref}
          onChange={({ floatValue }) => {
            onChange(floatValue);
          }}
        />
      )}
    />
  );
});

const CurrencyBase = forwardRef<
  React.ElementRef<typeof Input>,
  Readonly<CurrencyInputProps>
>(
  (
    {
      width = '100%',
      isDisabled = false,
      min = Number.MIN_SAFE_INTEGER,
      max = Number.MAX_SAFE_INTEGER,
      maxLength = 24,
      allowNegative = false,
      onFocus,
      InputProps,
      onChange,
      error,
      errorMessage,
      isInvalid = false,
      ...props
    },
    inputRef,
  ) => {
    return (
      <NumericFormat<MuiInputType>
        width={width}
        inputMode="decimal"
        customInput={Input}
        disabled={isDisabled}
        allowNegative={allowNegative}
        thousandSeparator
        onValueChange={onChange}
        fixedDecimalScale
        errorMessage={errorMessage}
        decimalScale={2}
        isInvalid={isInvalid}
        aria-invalid={isInvalid}
        error={error}
        isAllowed={({ floatValue, value }) => {
          if (floatValue === undefined) return true;
          return (
            floatValue <= max && floatValue >= min && value.length <= maxLength
          );
        }}
        getInputRef={inputRef}
        InputProps={{
          onFocus,
          startAdornment: <InputAdornment position="start">$</InputAdornment>,
          ...InputProps,
        }}
        {...props}
      />
    );
  },
);

const TanstackFormCurrency = forwardRef<
  React.ElementRef<typeof Input>,
  Readonly<CurrencyInputProps>
>(({ ...props }, ref) => {
  const field = useFieldContext<number | undefined>();
  const errorMessage = field.state.meta.errors[0] as string | undefined;
  return (
    <CurrencyBase
      {...props}
      ref={ref}
      value={field.state.value as any}
      error={field.state.meta.errors.length > 0}
      errorMessage={errorMessage}
      onChange={({ floatValue }) => field.handleChange(floatValue)}
      onBlur={field.handleBlur}
    />
  );
});

export default CurrencyInput;
